import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  END_PRESENCE,
  ThemeMenuItems,
  ThemeToggleButton,
  useHostAuth,
} from '../shared/ui';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bell } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { environment } from '../../environments/environment';
import { PAGE_WIDTH } from './page-width';
import { NOTIFICATION_SUMMARY } from '../features/notifications/graphql';
import { useDistributor } from '../providers/use-distributor';

const NAV_LINK =
  'rounded pl-3 pr-3 py-1.5 text-sm text-hilevel-slate no-underline hover:bg-hilevel-light';
const NAV_LINK_ACTIVE = `${NAV_LINK} bg-hilevel-light font-semibold`;

const DROPDOWN_ITEM =
  'block w-full cursor-pointer border-0 bg-transparent pl-3 pr-3 py-1.5 text-left text-sm ' +
  'text-hilevel-slate no-underline data-[focus]:bg-hilevel-light';

/** The bell and the theme switch: an icon on its own, no button chrome. */
const ICON_BUTTON =
  'cursor-pointer bg-transparent px-1 text-hilevel-slate no-underline hover:text-hilevel-red';

/** Long company names are trimmed rather than pushing the menu wide. */
function trimCompanyName(name: string): string {
  const length = 15;
  return name.length > length ? `${name.substring(0, length - 3)}...` : name;
}

export function MenuBar({
  onPrefetchQuote,
}: {
  /** Called when someone points at Quote, to start its chunk early. */
  onPrefetchQuote?: () => void;
} = {}) {
  const { user, isAuthenticated } = useHostAuth();
  const { loginWithRedirect, logout } = useAuth0();
  const [endPresence] = useMutation(END_PRESENCE, {
    variables: { app: 'distributor-portal' },
  });
  const distributor = useDistributor();
  const [open, setOpen] = useState(false);

  // Announcements that have not expired. `today` is fixed for the life of the
  // component so the query is not refetched on every render.
  const today = useMemo(() => new Date().toISOString(), []);
  const { data } = useQuery(NOTIFICATION_SUMMARY, {
    variables: { today },
    skip: !isAuthenticated,
  });
  const notificationCount: number = (data as any)?.notificationSummary?.count ?? 0;

  return (
    <nav className="border-0 border-b border-solid border-hilevel-border bg-hilevel-offwhite">
      <div className={`${PAGE_WIDTH} flex items-center gap-3 pl-4 pr-4 py-2`}>
        <button
          type="button"
          className={`${BUTTON_SECONDARY} lg:hidden`}
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          ☰
        </button>

        <div className={`${open ? 'flex' : 'hidden'} flex-1 gap-1 lg:flex`}>
          <NavLink to="/" className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE : NAV_LINK)} end>
            Home
          </NavLink>
          <NavLink
            to="/quote"
            // Pointing at Quote, or tabbing onto it, starts the drawing engine
            // downloading — usually a big enough head start that the click has
            // nothing to wait for.
            onPointerEnter={() => onPrefetchQuote?.()}
            onFocus={() => onPrefetchQuote?.()}
            className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE : NAV_LINK)}
          >
            Quote
          </NavLink>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {!isAuthenticated ? (
            <>
              {/* Signed in, the theme is three named rows in the dropdown
                  below. There is no dropdown here, and the pages reachable
                  signed out — support, the terms — still have to be readable
                  in the theme somebody set on their last visit. */}
              <ThemeToggleButton className={ICON_BUTTON} />
              <button
                type="button"
                className={BUTTON_SECONDARY}
                onClick={() => loginWithRedirect()}
              >
                Login
              </button>
              {/* Support is reachable signed out on purpose: somebody locked
                  out of their account is exactly who needs it. */}
              <Link to="/support" className={`${BUTTON_PRIMARY} no-underline`}>
                Support
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/notifications"
                className={`${ICON_BUTTON} relative`}
                aria-label={
                  notificationCount > 0
                    ? `Notifications, ${notificationCount} unread`
                    : 'Notifications'
                }
              >
                <Bell />
                {notificationCount > 0 && (
                  <span className="absolute -right-2 -top-1 rounded-full bg-hilevel-red pl-1.5 pr-1.5 text-[10px] font-semibold text-white">
                    {notificationCount}
                  </span>
                )}
              </Link>

              <Menu as="div" className="relative">
                <MenuButton className={BUTTON_SECONDARY}>
                  {user?.picture && (
                    <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                  )}
                  {user?.name}
                </MenuButton>
                <MenuItems
                  modal={false}
                  className="absolute right-0 z-50 mt-1 w-56 rounded border-[1px] border-solid border-hilevel-border bg-white py-1 shadow-lg focus:outline-none"
                >
                  <MenuItem>
                    <Link to="/company-settings" className={DROPDOWN_ITEM}>
                      {distributor ? trimCompanyName(distributor.name) : 'Company'}
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link to="/user-settings" className={DROPDOWN_ITEM}>
                      Your Profile
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link to="/support" className={DROPDOWN_ITEM}>
                      Support
                    </Link>
                  </MenuItem>
                  <div className="my-1 border-0 border-t border-solid border-hilevel-border" />
                  <p className="pb-0.5 pl-3 pr-3 pt-1 text-[11px] font-semibold uppercase tracking-wide text-hilevel-gray">
                    Appearance
                  </p>
                  <ThemeMenuItems itemClassName={DROPDOWN_ITEM} />
                  <div className="my-1 border-0 border-t border-solid border-hilevel-border" />
                  <MenuItem>
                    <button
                      type="button"
                      className={DROPDOWN_ITEM}
                      onClick={async () => {
                        // Awaited, so the roster is told before the redirect
                        // cancels the request — and caught, so a presence
                        // failure can never be the thing that stops somebody
                        // signing out.
                        await endPresence().catch(() => undefined);
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                    >
                      Sign out
                    </button>
                  </MenuItem>
                  <div className="my-1 border-0 border-t border-solid border-hilevel-border" />
                  <div className="pl-3 pr-3 py-1.5 text-xs text-hilevel-gray">
                    {environment.version}
                  </div>
                </MenuItems>
              </Menu>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
